import {Injectable} from '@angular/core';
import {
    BackendOberflaecheService,
    Helper,
    IRateService,
    OberflaecheElement,
    RateService,
    TarifierungProvider,
    TarifierungResponse,
} from '@vhv/tarifrechner-common';
import type { BaugeraeteFachdaten } from '../model/baugeraete-fachdaten'
import {RateConfigService} from './rate-config.service';
import { formatDate } from '@angular/common';
import {BaugeraeteTarifrechnerDaten} from "../model/baugeraete-tarifrechner-daten";

@Injectable({
    providedIn: 'root',
})
export class RateBaugeraeteService implements IRateService {

    private cachedRate: TarifierungResponse;

    private isDirektionsanfrage: boolean = false;

    private rateChangeCallbackFn: Function;

    constructor(
        private rateService: RateService,
        private rateConfigService: RateConfigService,
        private tarifierungProvider: TarifierungProvider,
        private backendOberflaecheService: BackendOberflaecheService,
    ) {
        this.rateService.setRateService(this);
        this.backendOberflaecheService.getOberflaeche()
            .then((value: any) => {
                this.backendOberflaecheService
                    .getElementByName('IstDirektionsanfrage')
                    .registerObservable()
                    .subscribe(this.direktionsanfrageObserverFn);
            })
            .catch((err: any) => {
                console.error('Oberflaeche konnte fuer Baugeraete-Rate-Service nicht gelesen werden', err);
            });
    }

    // Funktion, die vom Element gerufen wird, wenn es sich geaendert hat.
    direktionsanfrageObserverFn = (element: OberflaecheElement): void => {
        // uebergebene Element pruefen und Direktionsanfrage merken und dann die callBack-Funktion
        // aufrufen. Diese aendert dann die Sidebar.
        if (this.backendOberflaecheService.getElementByName('IstDirektionsanfrage').value === 'true') {
            this.isDirektionsanfrage = true;
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
        } else {
            this.isDirektionsanfrage = false;
            if (!this.cachedRate) {
                this.calculateBeitrag();
            } else {
                this.callRateChangedCallbackFn(this.cachedRate, this.isDirektionsanfrage);
            }
        }
    };

    // Zentrale Funktion, um den Callback auszufuehren und hier
    // nochmals auf Direktionsanfrage zu pruefen, um parallele
    // Requests einzufangen.
    callRateChangedCallbackFn(value: TarifierungResponse, isDirektionsanfrage: boolean): void {
        if (isDirektionsanfrage) {
            this.rateChangeCallbackFn(null, isDirektionsanfrage);
        } else {
            this.rateChangeCallbackFn(value, isDirektionsanfrage);
        }
    }

    getCachedBeitrag(): TarifierungResponse {
        if (this.backendOberflaecheService.getElementByName('IstDirektionsanfrage').value === 'true') {
            return null;
        }
        return this.cachedRate;
    }

    getCachedBausteinBeitrag(bausteinName: string): TarifierungResponse {
        return null;
    }

    public registerCallback(callbackFn: Function): void {
        this.rateChangeCallbackFn = callbackFn;
    }

    calculateBeitrag(): void {
        if (this.isDirektionsanfrage) {
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
        } else {
            this.getBeitrag().then(value => {
                this.callRateChangedCallbackFn(value, this.isDirektionsanfrage);
            }).catch(error => {
                console.debug('Beitrag kann nicht ermittelt werden: ' + error);
            });
        }
    }

    getBausteinBeitrag(bausteinName: string): Promise<TarifierungResponse> {
        if (this.isDirektionsanfrage) {
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
            return null;
        } else {
            return this.tarifierungProvider.getTarifierungBeitragP(
                'test',
                'JAEHRLICH',
                '',
                this.getBeitragBaugeraeteFachdaten(true),
            );
        }
    }


    public getBeitrag(): Promise<TarifierungResponse> {
        if (this.isDirektionsanfrage) {
            this.callRateChangedCallbackFn(null, this.isDirektionsanfrage);
            return Promise.resolve(null);
        }

        return this.tarifierungProvider.getTarifierungBeitragP(
            'test',
            'JAEHRLICH',
            '',
            this.getBeitragBaugeraeteFachdaten(),
        );

    }

    public getFachdaten(): any {
        return this.getBaugeraeteFachdaten();
    }

    //NEUE KRITERIEN HIER!
    public getBaugeraeteFachdaten(alleBausteine?: boolean): BaugeraeteFachdaten {
        const ausgefuellteWerte: BaugeraeteTarifrechnerDaten = this.backendOberflaecheService.getAusgefuellteWerte() as BaugeraeteTarifrechnerDaten;

        return {
            azvm: ausgefuellteWerte.Azvm,
            vermittlernummer: ausgefuellteWerte.Vermittlernummer,
            vermietet: Helper.boolify(this.checkEmpty(ausgefuellteWerte.Vermietet)),
            downloadZeitstempel: ausgefuellteWerte.DownloadZeitstempel,

            versicherungsbeginn: this.checkVersicherungsbeginn(ausgefuellteWerte.Versicherungsbeginn),

            kaufpreis: Helper.parseMaskedEuro(ausgefuellteWerte.Kaufpreis),
            anschaffungsjahr: this.checkEmpty(ausgefuellteWerte.Anschaffungsjahr),
            vorsteuerabzugsberechtigt: Helper.boolify(ausgefuellteWerte.Vorsteuerabzugsberechtigt),
            produktauswahl: this.checkEmpty(ausgefuellteWerte.Produktauswahl),
            baugeraet: ausgefuellteWerte.Baugeraet,
            monetaererSelbstbehalt: ausgefuellteWerte.MonetaererSelbstbehalt,
            vertragsLaufzeit: ausgefuellteWerte.Vertragslaufzeit,
            hersteller: this.checkEmpty(ausgefuellteWerte.Hersteller),
            fabriknummer: this.checkEmpty(ausgefuellteWerte.Fabriknummer),
            typbezeichnung: this.checkEmpty(ausgefuellteWerte.Typbezeichnung),
            betriebsart: this.checkBetriebsart(ausgefuellteWerte.Betriebsart),

            hatVorversicherung: Helper.boolify(this.checkEmpty(ausgefuellteWerte.HatVorversicherung)),
            vorversicherer: this.checkEmpty(ausgefuellteWerte.Vorversicherer),
            vorversicherungGekuendigt: this.checkEmpty(ausgefuellteWerte.VorversicherungGekuendigt),
            vorschaeden: this.checkEmpty(ausgefuellteWerte.Vorschaeden),
            vorschaedenHoehe: ausgefuellteWerte.VorschaedenHoehe == "" ? 0 : Helper.parseMaskedNumber(ausgefuellteWerte.VorschaedenHoehe),

            abwAnschriftStrasse: ausgefuellteWerte.AbwAnschriftStrasse,
            abwAnschriftHausnummer: ausgefuellteWerte.AbwAnschriftHausnummer,
            abwAnschriftPLZ: ausgefuellteWerte.AbwAnschriftPLZ,
            abwAnschriftOrt: ausgefuellteWerte.AbwAnschriftOrt
        };
    }

    //Behebt Fehler, dass wenn Pflichtfelder noch nicht aufgefüllt sind, keine Berechnung des Beitrags statt finden kann. Ist das Feld leer, wird n/a zurückgegeben
    private checkEmpty(value:string): string{
        return value == "" ? "n/a" : value;
    }

    //Behebt ein Validierungsfeher
    private checkBetriebsart(value:string): string{
        switch(value) {
            case "":
            case "40700":
            case "40000":
            case "40500":
                return "n/a";
            default:
                return value;
        }
    }

    // Prüft, ob der Versicherungsbeginn gesetzt ist. (Notwendig für die Berechnung)
    private checkVersicherungsbeginn(versicherungsbeginn:string): string {
        if (!versicherungsbeginn) {
            const dummyVersicherungsbeginn = new Date();
            return formatDate(dummyVersicherungsbeginn.setDate(dummyVersicherungsbeginn.getDate() + 1), 'yyyy-MM-dd', 'de');
        }
        return versicherungsbeginn;
    }

    private getBeitragBaugeraeteFachdaten(alleBausteine?: boolean): BaugeraeteFachdaten {
        const tarifierungsdaten = this.getBaugeraeteFachdaten(alleBausteine);

        // Das folgenden Felder müssen für die Validatoren ausgefüllt sein, fülle mit Dummy-Wert für Beitragsberechnung
        tarifierungsdaten.downloadZeitstempel = 'n/a';
        if (!tarifierungsdaten.versicherungsbeginn) {
            const dummyVersicherungsbeginn = new Date();
            tarifierungsdaten.versicherungsbeginn = formatDate(dummyVersicherungsbeginn.setDate(dummyVersicherungsbeginn.getDate() + 1), 'yyyy-MM-dd', 'de');
        }

        return tarifierungsdaten;
    }

    private handleVHV(
        rateConfigService: RateConfigService,
        tarifierungProvider: TarifierungProvider,
    ): void {
        this.getBeitrag()
            .then(value => {
                const beitrag = value.bruttoBeitragGemZahlweise;
                console.debug(beitrag, this);
            })
            .catch(error => {
                console.error(error);
            });
    }
}
